import React, { useEffect, useState } from 'react';
import {
  Button,
  Typography,
  TextField,
  Menu,
  IconButton,
} from '@material-ui/core';
import { Formik } from 'formik';
import { format } from 'date-fns';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import { createFilterOptions } from '@material-ui/lab/Autocomplete';
import { useRootStore } from '../../stores/BridgeProvider';
import {
  StyledForm,
  ButtonFlex,
  ErrorText,
  FormRow,
  RowItem,
} from '../styles/styles';
import { DealStatusEnumsObj } from '../common/Enums';
import { DealSchema } from '../../validation';
import { safeFalsy, safeEval, getDefaultAgent } from '../util/UtilFunctions';
import Spinner from '../util/Spinner';
import { ListboxComponent } from '../util/ReactSelectOverrides';
import ACDropdown from '../util/ACDropdown';

const DealForm = ({
  onSubmit,
  deal = {},
  buttons,
  menuItems,
  submitLabel = 'Submit',
}) => {
  const { dataStore, authStore, orgStore, dealMapperStore } = useRootStore();
  const [isLoading, setLoading] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const { contacts } = dataStore;
  const { userContactId } = orgStore;
  const open = Boolean(anchorEl);

  const filter = createFilterOptions({
    limit: 80,
  });

  const dealMapSections = dealMapperStore.dealMapperViewStore.dealMapForView;

  const handleClick = e => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    const initialize = async () => {
      const requests = [];
      if (contacts.length === 0) {
        requests.push(dataStore.retrieveContacts());
      }
      await Promise.all(requests);
      setLoading(false);
    };
    initialize();
  }, []);

  if (isLoading) return <Spinner />;

  const handleInitialDate = dateType => {
    if (!deal.DealId) {
      return format(new Date(), 'YYYY-MM-DD');
    }
    return dateType ? format(dateType, 'YYYY-MM-DD') : null;
  };
  const defaultAgentId = () => {
    if (deal.DealId) {
      if (deal.AgentContactId) return deal.AgentContactId;
      const defaultAgent = getDefaultAgent(
        deal.DealId,
        dealMapSections,
        dataStore,
      );

      if (defaultAgent?.ContactId) return defaultAgent.ContactId;
    }

    return userContactId;
  };
  return (
    <Formik
      validationSchema={DealSchema}
      initialValues={{
        DealId: safeFalsy(deal.DealId),
        DealNumber: safeFalsy(deal.DealNumber, 'AUTO-GENERATE'),
        Title: safeFalsy(deal.Title),
        Description: safeFalsy(deal.Description),
        ContractDate: handleInitialDate(deal.ContractDate),
        TransactionDate: handleInitialDate(deal.TransactionDate),
        BillableDate: handleInitialDate(deal.BillableDate),
        PayableDate: handleInitialDate(deal.PayableDate),
        ApprovedBy: safeFalsy(deal.ApprovedBy, null),
        StatusId: safeFalsy(deal.StatusId, '0').toString(),
        AgentContactId: safeFalsy(defaultAgentId(), null),
        AgentContact: dataStore.getContactFromId(defaultAgentId()),
      }}
      onSubmit={async (values, actions) => {
        try {
          await onSubmit(values);
          const { AgentContact } = values;
          if (AgentContact && !AgentContact.IsAgent) {
            dataStore.retrieveContacts();
          }
        } catch (e) {
          console.error(e);
        }
        actions.setSubmitting(false);
      }}
      render={({
        handleBlur,
        handleChange,
        errors,
        handleSubmit,
        setFieldValue,
        values,
        isSubmitting,
        dirty,
        touched,
      }) => {
        const showError = val => errors[val] && touched[val];

        const handleCreateContact = async inputValue => {
          try {
            const { data: contactData } = await authStore.request.post(
              `${process.env.REACT_APP_API}/api/contact`,
              {
                Name: inputValue,
                IsAgent: 1,
              },
            );
            const { id: cId } = contactData;
            await dataStore.retrieveContacts();
            const contact = dataStore.getContactFromId(cId);
            setFieldValue('AgentContact', contact);
            setFieldValue('AgentContactId', cId);
          } catch (e) {
            console.error(e);
          }
        };

        return (
          <div>
            <div
              className="df-header"
              style={{ display: 'flex', alignItems: 'center' }}
            >
              <Typography variant="h2" style={{ margin: '0 auto' }}>
                {deal.DealId ? 'Edit Deal' : 'New Deal'}
              </Typography>
              {menuItems && (
                <div>
                  <IconButton onClick={handleClick} size="small">
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                  <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
                    {menuItems.map(item => item)}
                  </Menu>
                </div>
              )}
            </div>
            <StyledForm onSubmit={handleSubmit}>
              <TextField
                name="Title"
                label={
                  <div>
                    Deal Title <span style={{ color: 'red' }}>*</span>
                  </div>
                }
                value={values.Title}
                margin="normal"
                onChange={handleChange}
                onBlur={handleBlur}
                variant="outlined"
                fullWidth
              />
              {showError('Title') && <ErrorText>{errors.Title}</ErrorText>}
              <FormRow>
                <RowItem>
                  <ACDropdown
                    autoHighlight={false}
                    disableClearable={false}
                    textFieldProps={{ margin: 'normal' }}
                    value={values.AgentContact || null}
                    handleHomeEndKeys
                    ListboxComponent={ListboxComponent}
                    options={contacts.sort((a, b) => {
                      const agentCount = b.IsAgent - a.IsAgent;
                      if (agentCount) return agentCount;
                      return a.Name > b.Name ? 1 : -1;
                    })}
                    getOptionLabel={option => option.Name}
                    renderOption={({ isNew, Name }) =>
                      isNew ? `Add "${Name}"` : Name
                    }
                    getOptionSelected={(option, val) =>
                      option.ContactId === val.ContactId
                    }
                    label="Agent"
                    onChange={async (event, newValue) => {
                      if (newValue === null) {
                        setFieldValue('AgentContact', null);
                        setFieldValue('AgentContactId', null);
                      } else if (newValue !== null && newValue.ContactId) {
                        setFieldValue('AgentContact', newValue);
                        setFieldValue(
                          'AgentContactId',
                          safeEval(() => newValue.ContactId),
                          null,
                        );
                      } else if (newValue && newValue.inputValue) {
                        // Create a new value from the user input
                        handleCreateContact(newValue.inputValue);
                      }
                    }}
                    filterOptions={(options, params) => {
                      const filtered = filter(options, params);

                      const inputValueIsNotAContact =
                        options.findIndex(
                          option => option.Name === params.inputValue,
                        ) < 0;

                      // Suggest the creation of a new value
                      if (params.inputValue !== '' && inputValueIsNotAContact) {
                        filtered.push({
                          inputValue: params.inputValue,
                          Name: params.inputValue,
                          isNew: true,
                        });
                      }

                      return filtered;
                    }}
                  />
                </RowItem>
              </FormRow>
              <FormRow>
                <RowItem>
                  <TextField
                    name="ContractDate"
                    label={
                      <div>
                        Contract Date <span style={{ color: 'red' }}>*</span>
                      </div>
                    }
                    margin="normal"
                    value={values.ContractDate}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    variant="outlined"
                    fullWidth
                    type="date"
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                  {showError('ContractDate') && (
                    <ErrorText>{errors.ContractDate}</ErrorText>
                  )}
                </RowItem>
                <RowItem>
                  <TextField
                    name="TransactionDate"
                    label={
                      <div>
                        Transaction Date <span style={{ color: 'red' }}>*</span>
                      </div>
                    }
                    margin="normal"
                    value={values.TransactionDate}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    variant="outlined"
                    fullWidth
                    // error={showError('TransactionDate')}
                    type="date"
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                  {showError('TransactionDate') && (
                    <ErrorText>{errors.TransactionDate}</ErrorText>
                  )}
                </RowItem>
              </FormRow>
              <FormRow>
                <RowItem>
                  <TextField
                    name="BillableDate"
                    label={
                      <div>
                        Billable Date <span style={{ color: 'red' }}>*</span>
                      </div>
                    }
                    margin="normal"
                    value={values.BillableDate}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    variant="outlined"
                    fullWidth
                    // error={showError('BillableDate')}
                    type="date"
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                  {showError('BillableDate') && (
                    <ErrorText>{errors.BillableDate}</ErrorText>
                  )}
                </RowItem>
                <RowItem>
                  <TextField
                    name="PayableDate"
                    label={
                      <div>
                        Payable Date <span style={{ color: 'red' }}>*</span>
                      </div>
                    }
                    margin="normal"
                    value={values.PayableDate}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    variant="outlined"
                    fullWidth
                    // error={showError('PayableDate')}
                    type="date"
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                  {showError('PayableDate') && (
                    <ErrorText>{errors.PayableDate}</ErrorText>
                  )}
                </RowItem>
              </FormRow>
              <TextField
                name="Description"
                label="Description"
                margin="normal"
                value={values.Description}
                onChange={handleChange}
                onBlur={handleBlur}
                variant="outlined"
                fullWidth
                error={showError('Description')}
              />
              <FormRow>
                <ACDropdown
                  style={{ width: 'inherit', margin: '16px 0 8px' }}
                  disableClearable
                  name="StatusId"
                  value={DealStatusEnumsObj.find(
                    s => s.value === values.StatusId,
                  )}
                  options={DealStatusEnumsObj}
                  onChange={(e, v) => {
                    v !== null && setFieldValue('StatusId', v.value);
                  }}
                  getOptionLabel={option => {
                    return option.label || null;
                  }}
                  getOptionSelected={(option, value) => {
                    if (value === '') {
                      return true;
                    }
                    if (option.value === value.value) {
                      return true;
                    }
                  }}
                  label={
                    <div>
                      Deal Status <span style={{ color: 'red' }}>*</span>
                    </div>
                  }
                />
                <TextField
                  name="DealNumber"
                  label="Deal Number"
                  value={values.DealNumber}
                  margin="normal"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  variant="outlined"
                  fullWidth
                  error={showError('DealNumber')}
                />
              </FormRow>
              {showError('StatusId') && (
                <ErrorText>{errors.StatusId}</ErrorText>
              )}
              <ButtonFlex>
                <Button
                  color="primary"
                  variant="contained"
                  type="submit"
                  size="large"
                  disabled={!dirty || isSubmitting}
                  style={{
                    margin: '5px',
                  }}
                >
                  {submitLabel}
                </Button>
                {buttons && buttons.map(button => button)}
              </ButtonFlex>
            </StyledForm>
          </div>
        );
      }}
    />
  );
};

export default DealForm;
